<template>
  <div
    class="rounded-lg border border-gray-200 bg-gray-50 p-3 shadow-sm dark:border-gray-700 dark:bg-gray-800 dark:text-white">
    <div class="table-header">
      <h4 class="text-surface-400 font-medium tracking-wide text-gray-400">Photometric Analysis</h4>

      <p class="mb-1 text-xs text-gray-500">Threshold range: {{ minValue }} - {{ maxValue }} lux</p>
      <p class="mb-3 text-xs text-gray-500">(red = over {{ maxValue }}lx threshold)</p>
    </div>

    <div class="overflow-x-auto overflow-y-auto">
      <table class="w-full border-collapse text-xs">
        <thead>
          <tr class="border-b border-gray-200 bg-gray-50 dark:border-gray-600 dark:bg-gray-700">
            <th class="px-1 py-1 text-left font-medium text-gray-600 dark:text-gray-200">Colour</th>
            <th class="px-1 py-1 text-left font-medium text-gray-600 dark:text-gray-200">Lux</th>
            <th class="px-1 py-1 text-left font-medium text-gray-600 dark:text-gray-200">Level</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="(item, index) in spectrumData.slice().reverse()"
            :key="index"
            :class="[
              'cursor-pointer border-b border-gray-700 transition-colors',
              item.isOverThreshold ? 'border-red-200 bg-fuchsia-500' : '',
              hoveredIntensity === item.intensity
                ? 'border-blue-300 bg-blue-100 dark:border-blue-600 dark:bg-blue-900'
                : 'hover:bg-gray-500 dark:hover:bg-gray-700',
            ]"
            @mouseenter="handleRowHover(item.intensity)"
            @mouseleave="handleRowLeave">
            <td class="px-1 py-1">
              <div class="flex items-center gap-1">
                <div
                  class="h-3 w-3 rounded border border-gray-600 shadow-sm"
                  :style="{ backgroundColor: item.color }"></div>
              </div>
            </td>
            <td class="px-1 py-1 text-gray-200">
              {{ item.lux }}
            </td>
            <td class="px-1 py-1 text-gray-200">
              {{ item.description }}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script setup>
import { useCruxModuleStore } from '@/stores/api/cruxmodules.js';
import { updatePhotometricHoverIntensity } from '@/threejs/core';
import { generatePhotometricSpectrum } from '@/utils/photometricUtils';
import { computed, onUnmounted, ref } from 'vue';

const cruxModuleStore = useCruxModuleStore();

// Use user-defined threshold from settings (not calculated scene max)
const maxValue = computed(() => {
  return cruxModuleStore.getSettingValue('viewport_settings', 'render.photometricMaxValue', 2000);
});

const minValue = computed(() => {
  return 0; // Always start from 0
});

const steppedColors = computed(() => {
  return cruxModuleStore.getSettingValue('viewport_settings', 'render.photometricSteppedColors', false);
});

const spectrumData = computed(() => {
  return generatePhotometricSpectrum(steppedColors.value, maxValue.value);
});

const gradientStyle = computed(() => {
  const colors = spectrumData.value.map((item) => item.color);

  if (steppedColors) {
    // Create stepped gradient with hard stops
    const steps = colors
      .map((color, index) => {
        const position = (index / (colors.length - 1)) * 100;
        const nextPosition = ((index + 1) / (colors.length - 1)) * 100;
        return `${color} ${position}%, ${color} ${Math.min(nextPosition, 100)}%`;
      })
      .join(', ');
    return `linear-gradient(to right, ${steps})`;
  } else {
    // Smooth gradient (purple to red to match screenshot)
    return `linear-gradient(to right, ${colors.join(', ')})`;
  }
});

const hoveredIntensity = ref(null);

const handleRowHover = (intensity) => {
  hoveredIntensity.value = intensity;
  updatePhotometricHoverIntensity(parseFloat(intensity));
};

const handleRowLeave = () => {
  hoveredIntensity.value = null;
  updatePhotometricHoverIntensity(-1.0); // Reset to show all intensities
};

// Clean up when component is unmounted
onUnmounted(() => {
  handleRowLeave();
});
</script>
